import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save, User, Linkedin, Trash2, RefreshCw, ShieldCheck, ShieldAlert,
  Unplug, ChevronDown, KeyRound, Zap, Globe, ExternalLink,
} from 'lucide-react';
import { useProject } from '../../../contexts/ProjectContext';
import { contentApi, type Author, type ChannelCredential } from '../../../api/content';
import { useLinkedInStatus } from '../../../hooks/useLinkedInStatus';
import { ConnectLinkedInModal } from '../../../components/linkedin/ConnectLinkedInModal';
import {
  PanelSection,
  Input,
  Textarea,
  Button,
  Card,
  Skeleton,
  SkeletonAvatar,
  SkeletonText,
} from '../../../components/ui';
import { StatusPill } from '@archieai/ui';
import { SettingsPageLayout } from '../../../components/layout';

// ─── LinkedIn Connection Card ─────────────────────────────────────────────────

function LinkedInStatusPulse({ connected }: { connected: boolean }) {
  return (
    <span className="relative flex h-2.5 w-2.5">
      {connected && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
      )}
      <span
        className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
          connected ? 'bg-emerald-500' : 'bg-neutral-300'
        }`}
      />
    </span>
  );
}

function LinkedInConnectionCard({
  authorId,
  workspaceId,
  credential,
  credLoading,
  onConnect,
  onDisconnect,
  onRevalidate,
}: {
  authorId: string;
  workspaceId: string;
  credential: ChannelCredential | undefined;
  credLoading: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onRevalidate: () => void;
}) {
  const { status, loading: statusLoading, refresh } = useLinkedInStatus(authorId);
  const isConnected = !!(credential && credential.is_valid);
  const hasCredential = !!credential;

  // Re-check status when credential changes
  useEffect(() => { refresh(); }, [credential, refresh]);

  if (credLoading || statusLoading) {
    return (
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <div className="flex items-center gap-3">
          <Skeleton variant="circular" width={40} height={40} />
          <div className="flex-1 space-y-2">
            <Skeleton variant="text" width="50%" height={16} />
            <Skeleton variant="text" width="30%" height={12} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border-2 transition-all duration-300 ${
        isConnected
          ? 'border-emerald-200 bg-gradient-to-br from-emerald-50/60 via-white to-blue-50/40'
          : hasCredential
            ? 'border-amber-200 bg-gradient-to-br from-amber-50/40 via-white to-neutral-50'
            : 'border-neutral-200 bg-gradient-to-br from-neutral-50 via-white to-blue-50/20'
      }`}
    >
      {/* Status header */}
      <div className="flex items-center justify-between p-5 pb-4">
        <div className="flex items-center gap-3.5">
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-lg ${
              isConnected
                ? 'bg-[#0A66C2]'
                : 'bg-neutral-200'
            }`}
          >
            <Linkedin size={20} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-neutral-900">LinkedIn</span>
              <LinkedInStatusPulse connected={isConnected} />
            </div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
              {isConnected
                ? status?.profileName
                  ? `Connected as ${status.profileName}`
                  : 'Connected'
                : hasCredential
                  ? 'Credentials stored but invalid'
                  : 'Not connected'}
            </div>
          </div>
        </div>

        {/* Right side — badge + actions */}
        <div className="flex items-center gap-2">
          {isConnected ? (
            <>
              <StatusPill variant="success" label="Active" showDot />
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<RefreshCw size={12} />}
                onClick={() => { refresh(); onRevalidate(); }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                Validate
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Unplug size={12} />}
                onClick={onDisconnect}
                className="text-neutral-400 hover:text-red-500"
              >
                Disconnect
              </Button>
            </>
          ) : hasCredential ? (
            <>
              <StatusPill variant="warning" label="Invalid" showDot />
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 size={12} />}
                onClick={onDisconnect}
                className="text-neutral-400 hover:text-red-500"
              />
            </>
          ) : null}
        </div>
      </div>

      {/* Validation details (when connected) */}
      {hasCredential && (
        <div className="mx-5 mb-4 flex items-center gap-4 text-[11px]">
          {credential.is_valid ? (
            <span className="inline-flex items-center gap-1 text-emerald-600">
              <ShieldCheck size={12} /> Validated
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-amber-600">
              <ShieldAlert size={12} /> Needs re-validation
            </span>
          )}
          {credential.last_validated_at && (
            <span className="text-neutral-400">
              Last checked {new Date(credential.last_validated_at).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
              })}
            </span>
          )}
          {credential.expires_at && (
            <span className="text-neutral-400">
              Expires {new Date(credential.expires_at).toLocaleDateString()}
            </span>
          )}
          {status?.profileUrn && (
            <span className="font-mono text-neutral-400 truncate max-w-[200px]">{status.profileUrn}</span>
          )}
        </div>
      )}

      {/* Connect CTA (when not connected) */}
      {!hasCredential && (
        <div className="px-5 pb-5">
          <button
            onClick={onConnect}
            className="group relative w-full overflow-hidden rounded-lg bg-[#0A66C2] px-5 py-3 text-white transition-all hover:bg-[#004182] active:scale-[0.99]"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
            <div className="relative flex items-center justify-center gap-2.5">
              <Zap size={16} />
              <span className="font-medium text-sm">Connect with LinkedIn</span>
            </div>
            <div className="relative text-[11px] text-blue-200 mt-0.5">
              Secure browser session powered by Browserbase
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Manual Credential Fallback ───────────────────────────────────────────────

function ManualCredentialForm({
  onSave,
  saving,
  error,
}: {
  onSave: (li_at: string, jsessionid: string) => void;
  saving: boolean;
  error: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [li_at, setLiAt] = useState('');
  const [jsessionid, setJsessionid] = useState('');

  return (
    <div className="mt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-[11px] text-neutral-400 hover:text-neutral-600 transition-colors"
      >
        <KeyRound size={11} />
        <span>Advanced: Paste cookies manually</span>
        <ChevronDown
          size={11}
          className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {expanded && (
        <div className="mt-3 p-4 rounded-lg border border-dashed border-neutral-300 bg-neutral-50/50 space-y-3">
          <p className="text-[11px] text-neutral-500 leading-relaxed">
            If Browserbase isn&apos;t available, paste your LinkedIn <code className="px-1 py-0.5 rounded bg-neutral-200/70 font-mono text-[10px]">li_at</code> and <code className="px-1 py-0.5 rounded bg-neutral-200/70 font-mono text-[10px]">JSESSIONID</code> cookies
            from your browser&apos;s developer tools. These are encrypted before storage.
          </p>
          <Input
            label="li_at cookie"
            type="password"
            value={li_at}
            onChange={e => setLiAt(e.target.value)}
            size="sm"
            className="font-mono text-xs"
            placeholder="AQE..."
          />
          <Input
            label="JSESSIONID cookie"
            type="password"
            value={jsessionid}
            onChange={e => setJsessionid(e.target.value)}
            size="sm"
            className="font-mono text-xs"
            placeholder="ajax:..."
          />
          {error && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
              {error}
            </div>
          )}
          <div className="flex justify-end">
            <Button
              size="sm"
              loading={saving}
              disabled={!li_at || !jsessionid}
              onClick={() => onSave(li_at, jsessionid)}
            >
              Save & Validate
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const AuthorDetailPage = () => {
  const { authorId } = useParams<{ authorId: string }>();
  const navigate = useNavigate();
  const { currentProject, buildProjectUrl } = useProject();
  const workspaceId = currentProject?.workspaceId;
  const [author, setAuthor] = useState<Author | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!workspaceId || !authorId) return;
    setLoading(true);
    contentApi.getAuthor(workspaceId, authorId)
      .then(setAuthor)
      .catch(() => navigate(buildProjectUrl('au', 'sm')))
      .finally(() => setLoading(false));
  }, [workspaceId, authorId, navigate]);

  // ─── Credential Management ─────────────────────────────────────
  const [credentials, setCredentials] = useState<ChannelCredential[]>([]);
  const [credLoading, setCredLoading] = useState(false);
  const [credSaving, setCredSaving] = useState(false);
  const [credError, setCredError] = useState('');
  const [showConnectModal, setShowConnectModal] = useState(false);

  const fetchCredentials = useCallback(async () => {
    if (!workspaceId || !authorId) return;
    setCredLoading(true);
    try {
      const creds = await contentApi.getCredentials(workspaceId, authorId);
      setCredentials(creds);
    } catch {
      setCredentials([]);
    } finally {
      setCredLoading(false);
    }
  }, [workspaceId, authorId]);

  useEffect(() => { fetchCredentials(); }, [fetchCredentials]);

  const updateField = (field: string, value: unknown) => {
    setAuthor(prev => prev ? { ...prev, [field]: value } : null);
    setDirty(true);
  };

  const handleSave = async () => {
    if (!workspaceId || !authorId || !author) return;
    setSaving(true);
    try {
      const updated = await contentApi.updateAuthor(workspaceId, authorId, {
        name: author.name,
        email: author.email,
        role: author.role,
        bio: author.bio,
        tone_instructions: author.tone_instructions,
        writing_samples: author.writing_samples,
        linkedin_profile_url: author.linkedin_profile_url,
        linkedin_urn: author.linkedin_urn,
        avatar_url: author.avatar_url,
      } as Partial<Author>);
      setAuthor(updated);
      setDirty(false);
    } catch (err) {
      console.error('Failed to save', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCredentials = async (li_at: string, jsessionid: string) => {
    if (!workspaceId || !authorId) return;
    setCredSaving(true);
    setCredError('');
    try {
      const { validation_error } = await contentApi.storeCredentials(workspaceId, authorId, {
        li_at,
        jsessionid,
      });
      if (validation_error) {
        setCredError(`Saved but validation warning: ${validation_error}`);
      }
      await fetchCredentials();
    } catch (err) {
      setCredError((err as Error).message || 'Failed to save credentials');
    } finally {
      setCredSaving(false);
    }
  };

  const handleDeleteCredentials = async () => {
    if (!workspaceId || !authorId) return;
    try {
      await contentApi.deleteCredentials(workspaceId, authorId, 'linkedin');
      await fetchCredentials();
    } catch (err) {
      console.error('Failed to delete credentials', err);
    }
  };

  const handleConnected = useCallback(() => {
    // Refresh credentials after Browserbase connection completes
    fetchCredentials();
  }, [fetchCredentials]);

  const linkedInCred = credentials.find(c => c.platform === 'linkedin');

  if (loading || !author) {
    return (
      <SettingsPageLayout
        title="Author"
        subtitle="Loading author details..."
        icon={<User size={20} />}
        backTo={buildProjectUrl('au', 'sm')}
        backLabel="Contributors"
      >
        <div className="flex items-center gap-4 mb-8">
          <SkeletonAvatar size={64} />
          <div className="flex-1 space-y-2">
            <Skeleton variant="text" width="40%" height={24} />
            <Skeleton variant="text" width="25%" height={16} />
          </div>
        </div>
        <div className="space-y-6">
          {[1, 2, 3].map(i => (
            <Card key={i} padding="lg">
              <SkeletonText lines={4} />
            </Card>
          ))}
        </div>
      </SettingsPageLayout>
    );
  }

  return (
    <SettingsPageLayout
      title={author.name}
      subtitle={author.role || 'Author'}
      icon={<User size={20} />}
      backTo={buildProjectUrl('au', 'sm')}
      backLabel="Contributors"
      actions={
        <Button
          variant="default"
          size="sm"
          leftIcon={<Save size={14} />}
          loading={saving}
          disabled={!dirty}
          onClick={handleSave}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      }
    >
      {/* Profile header */}
      <div className="flex items-center gap-4 mb-8">
        <Avatar
          src={author.avatar_url || undefined}
          name={author.name}
          size="xl"
        />
        <div>
          <Input
            value={author.name}
            onChange={e => updateField('name', e.target.value)}
            className="text-xl font-bold text-neutral-800 border-none shadow-none ring-0 focus:ring-0 px-0"
            size="lg"
          />
          <div className="text-sm text-neutral-500">{author.role || 'No role set'}</div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Profile section */}
        <PanelSection
          title="Profile"
          icon={<User size={14} />}
          variant="card"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Email"
                type="email"
                value={author.email || ''}
                onChange={e => updateField('email', e.target.value || null)}
                size="sm"
              />
              <Input
                label="Role"
                type="text"
                value={author.role || ''}
                onChange={e => updateField('role', e.target.value || null)}
                size="sm"
              />
            </div>
            <Textarea
              label="Bio"
              value={author.bio || ''}
              onChange={e => updateField('bio', e.target.value || null)}
              rows={3}
              size="sm"
              placeholder="Short author bio..."
            />
          </div>
        </PanelSection>

        {/* Tone & Writing Style section */}
        <PanelSection
          title="Tone & Writing Style"
          variant="card"
          collapsible
          defaultCollapsed={false}
        >
          <div className="space-y-4">
            <Textarea
              label="Tone Instructions (for AI-generated content)"
              value={author.tone_instructions || ''}
              onChange={e => updateField('tone_instructions', e.target.value || null)}
              rows={5}
              size="sm"
              placeholder="Describe the author's writing style, tone, vocabulary preferences, sentence structure patterns..."
            />
            <Textarea
              label="Writing Samples (paste 3-5 example snippets, one per line)"
              value={Array.isArray(author.writing_samples) ? author.writing_samples.join('\n\n---\n\n') : ''}
              onChange={e => {
                const samples = e.target.value.split('\n\n---\n\n').filter(Boolean);
                updateField('writing_samples', samples);
              }}
              rows={8}
              size="sm"
              className="font-mono text-xs"
              placeholder="Paste sample posts here, separated by ---"
            />
          </div>
        </PanelSection>

        {/* ─── LinkedIn Connection (unified section) ─────────────────── */}
        <PanelSection
          title="LinkedIn"
          icon={<Linkedin size={14} className="text-[#0A66C2]" />}
          variant="card"
          actions={
            linkedInCred?.is_valid ? (
              <StatusPill variant="success" label="Connected" showDot />
            ) : linkedInCred ? (
              <StatusPill variant="warning" label="Needs attention" showDot />
            ) : null
          }
        >
          <div className="space-y-5">
            {/* Connection status card */}
            <LinkedInConnectionCard
              authorId={authorId!}
              workspaceId={workspaceId!}
              credential={linkedInCred}
              credLoading={credLoading}
              onConnect={() => setShowConnectModal(true)}
              onDisconnect={handleDeleteCredentials}
              onRevalidate={fetchCredentials}
            />

            {/* Profile fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Input
                  label="Profile URL"
                  type="url"
                  value={author.linkedin_profile_url || ''}
                  onChange={e => updateField('linkedin_profile_url', e.target.value || null)}
                  size="sm"
                  placeholder="https://linkedin.com/in/..."
                />
                {author.linkedin_profile_url && (
                  <a
                    href={author.linkedin_profile_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] text-[#0A66C2] hover:underline ml-1"
                  >
                    <ExternalLink size={9} /> View profile
                  </a>
                )}
              </div>
              <Input
                label="URN (Voyager API)"
                type="text"
                value={author.linkedin_urn || ''}
                onChange={e => updateField('linkedin_urn', e.target.value || null)}
                size="sm"
                className="font-mono text-xs"
                placeholder="urn:li:person:..."
              />
            </div>

            {/* Re-connect button when already connected */}
            {linkedInCred && (
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Globe size={13} />}
                  onClick={() => setShowConnectModal(true)}
                  className="text-neutral-500"
                >
                  Re-connect via Browserbase
                </Button>
              </div>
            )}

            {/* Manual fallback */}
            <ManualCredentialForm
              onSave={handleSaveCredentials}
              saving={credSaving}
              error={credError}
            />
          </div>
        </PanelSection>
      </div>

      {/* Browserbase Connect Modal */}
      {workspaceId && authorId && (
        <ConnectLinkedInModal
          open={showConnectModal}
          onClose={() => setShowConnectModal(false)}
          onConnected={handleConnected}
          authorId={authorId}
          workspaceId={workspaceId}
        />
      )}
    </SettingsPageLayout>
  );
};

export default AuthorDetailPage;
